import React, { useEffect, useState } from 'react';
import { AlertCircle, Undo2 } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { listBooks } from '../../../../../services/BooksService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import {
  BillingClient,
  CREDITABLE_INVOICE_STATUSES,
  InvoiceSummary,
  getInvoiceLines,
  listBillingClients,
  listInvoices
} from '../../../../../services/InvoicesService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { CreditNote, formatEuro } from '../masters/financeMasters';
import {
  DocumentLine,
  DocumentLinesEditor,
  FormSkeleton,
  JournalPreview,
  LineProduct,
  SearchSelect,
  formatDate,
  linesSubtotal,
  linesVat,
  newLine,
  today
} from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

interface CreditNoteFormProps {
  privacyMode: boolean;
  nextNumber: string;
  // Importe ya abonado por factura (id -> total de sus notas)
  creditedByInvoice: Record<string, number>;
  onSave: (note: CreditNote) => void;
  onClose: () => void;
}

export const CreditNoteForm = ({ privacyMode, nextNumber, creditedByInvoice, onSave, onClose }: CreditNoteFormProps) => {
  const { notify } = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [products, setProducts] = useState<LineProduct[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState(today());
  const [invoiceId, setInvoiceId] = useState('');
  const [lines, setLines] = useState<DocumentLine[]>([newLine()]);
  const [reason, setReason] = useState('');

  const [linesLoading, setLinesLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clientList, invoiceList, books, chart] = await Promise.all([
          listBillingClients(),
          listInvoices(),
          listBooks(),
          listChartOfAccounts()
        ]);
        if (cancelled) return;
        setClients(clientList);
        setInvoices(invoiceList.filter(i => CREDITABLE_INVOICE_STATUSES.includes(i.status) && i.total > 0));
        setProducts(books.map(b => ({ id: b.id, name: b.title, sku: b.isbn || b.sku, price: b.price, vat: 4 })));
        setAccounts(chart);
        setLoadError('');
      } catch (err) {
        console.error('Error loading credit note form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los datos de la nota de crédito.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const client = clients.find(c => c.id === clientId);
  const clientInvoices = invoices.filter(i => i.clientId === clientId);
  const invoice = invoices.find(i => i.id === invoiceId);
  const remaining = invoice ? invoice.total - (creditedByInvoice[invoice.id] || 0) : 0;

  const subtotal = linesSubtotal(lines);
  const vatTotal = linesVat(lines);
  const total = subtotal + vatTotal;

  // Asiento inverso al de la factura
  const journalLines: JournalLine[] = [
    { account: '700000', accountName: 'Ventas de mercaderías', debit: subtotal, credit: 0 },
    { account: '477000', accountName: 'H.P. IVA Repercutido', debit: vatTotal, credit: 0 },
    { account: '430000', accountName: 'Clientes (Euros)', debit: 0, credit: total }
  ];

  const handleClientChange = (id: string) => {
    setClientId(id);
    setInvoiceId('');
    setLines([newLine()]);
  };

  // Al elegir la factura se copian sus líneas, para ajustar después lo que se abona
  const handleInvoiceChange = async (id: string) => {
    setInvoiceId(id);
    if (!id) return;
    setLinesLoading(true);
    try {
      const invoiceLines = await getInvoiceLines(id);
      setLines(invoiceLines.length > 0
        ? invoiceLines.map(l => ({
            ...newLine(),
            description: l.description,
            quantity: l.quantity,
            price: l.unitPrice,
            vat: l.taxRate
          }))
        : [newLine()]);
    } catch (err) {
      console.error('Error loading invoice lines:', err);
      setFormError('No se pudieron cargar las líneas de la factura; añádalas a mano.');
    } finally {
      setLinesLoading(false);
    }
  };

  const validate = () => {
    if (!client) return 'Seleccione un cliente.';
    if (!date) return 'La fecha es obligatoria.';
    if (!invoice) return 'Seleccione la factura de origen.';
    if (!reason.trim()) return 'Indique el motivo de la nota de crédito.';
    if (lines.some(l => !l.description.trim())) return 'Todas las líneas necesitan una descripción.';
    if (total <= 0) return 'El importe de la nota de crédito debe ser mayor que cero.';
    if (total > remaining + 0.005) return `El abono no puede superar lo que queda de la factura ${invoice.number}: ${formatEuro(remaining, false)}.`;
    return '';
  };

  // Primero el asiento: la nota solo se guarda si el asiento se ha contabilizado
  const handleIssue = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      await postJournalEntry(`Nota de crédito ${nextNumber} sobre ${invoice.number} (${client.name})`, journalLines, 'Notas de Crédito', accounts, date);
    } catch (err) {
      console.error('Error posting credit note journal entry:', err);
      setFormError(`No se pudo contabilizar la nota de crédito: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    onSave({
      number: nextNumber,
      clientId,
      clientName: client.name,
      clientCif: client.cif,
      date,
      invoiceId: invoice.id,
      invoiceNumber: invoice.number,
      lines,
      reason: reason.trim(),
      subtotal,
      vat: vatTotal,
      total
    });
    notify('Nota de crédito emitida', `Nota ${nextNumber} sobre ${invoice.number} emitida y contabilizada en el Libro Diario [700 + 477 a 430].`, 'info');
    setSaving(false);
    onClose();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const clientOptions = clients
    .filter(c => invoices.some(i => i.clientId === c.id))
    .map(c => ({ value: c.id, label: c.name, sub: c.cif ? `CIF: ${c.cif}` : undefined }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">VENTAS Y COBROS • NOTAS DE CRÉDITO</span>
          <h3 className="fin-doc__title"><Undo2 size={14} /> Nota de crédito (abono)</h3>
        </div>
        <span className="fin-doc__badge">ASIENTO INVERSO</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cliente *</label>
          <SearchSelect
            options={clientOptions}
            value={clientId}
            onChange={handleClientChange}
            placeholder="Buscar cliente por nombre o CIF..."
            emptyText="No hay clientes con facturas emitidas"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Nº nota autonumérico</label>
          <input
            type="text"
            readOnly
            className="sidebar-form-input fin-doc__mono fin-doc__readonly"
            value={`${nextNumber} (Automático)`}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Factura de origen *</label>
          <select
            className="sidebar-form-input"
            value={invoiceId}
            disabled={!clientId || linesLoading}
            onChange={(e) => handleInvoiceChange(e.target.value)}
          >
            <option value="">{clientId ? 'Seleccione la factura...' : 'Elija antes el cliente'}</option>
            {clientInvoices.map(i => (
              <option key={i.id} value={i.id}>
                {i.number}{i.issueDate ? ` · ${formatDate(i.issueDate)}` : ''} · {formatEuro(i.total, privacyMode)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {linesLoading ? (
        <div className="fin-lines__empty">Cargando líneas de la factura...</div>
      ) : (
        <DocumentLinesEditor lines={lines} onChange={setLines} products={products} privacyMode={privacyMode} descriptionAsArticle />
      )}

      <div className="sidebar-form-group">
        <label className="sidebar-form-label">Motivo *</label>
        <textarea
          rows={2}
          className="sidebar-form-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Devolución de ejemplares defectuosos, rappel por volumen..."
        />
      </div>

      <div className="fin-doc__totals">
        <span>Subtotal: <strong>{formatEuro(subtotal, privacyMode)}</strong></span>
        <span>IVA: <strong>{formatEuro(vatTotal, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total abono: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving || linesLoading} onClick={handleIssue}>
          {saving ? 'Emitiendo...' : 'Emitir nota de crédito'}
        </button>
      </div>
    </div>
  );
};

export default CreditNoteForm;
